import { DateTime } from 'luxon';
import type { Locales } from '@/i18n/i18n-types';
import type { TenantHomeResponseDto } from '@/generated/korza-api-client/types.gen';
import type { ChartGranularity } from '@/lib/chart-axis';
import { bucketMergeChartData } from '@/lib/hooks/use-chart-data';

type HomePoint = TenantHomeResponseDto['revenueChart']['points'][number];

/** `date` = original api date of the bucket's first point (AD-011). */
export type HomeChartRow = {
  month: string;
  date: string;
  revenue: number;
  count: number;
};

/**
 * Início chart rows: one per bucket, summing `count` and `grossRevenueUsd`
 * (legacy name; the value is in the order currency).
 */
export function toHomeChartRows(
  points: HomePoint[],
  granularity: ChartGranularity,
  locale: Locales
): HomeChartRow[] {
  return bucketMergeChartData<HomePoint, { revenue: number; count: number }>(
    points,
    granularity,
    locale,
    (point, current) => ({
      revenue: (current?.revenue ?? 0) + point.grossRevenueUsd,
      count: (current?.count ?? 0) + point.count,
    }),
    (_iso, totals) => totals
  ).map((row) => ({
    month: row.month,
    date: String(row.date),
    revenue: Number(row.revenue),
    count: Number(row.count),
  }));
}

/** Same as `metrics/sales`.aov (AVG = SUM / COUNT); null without paid orders. */
export function computeAverageTicket(
  totalRevenue: number,
  totalPaidOrders: number
): number | null {
  return totalPaidOrders === 0 ? null : totalRevenue / totalPaidOrders;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Mobile Início: hour for periods up to 24h, day otherwise (or invalid dates). */
export function autoHomeGranularity(
  fromIso: string,
  toIso: string
): 'hour' | 'day' {
  const from = DateTime.fromISO(fromIso);
  const to = DateTime.fromISO(toIso);
  if (!from.isValid || !to.isValid) return 'day';
  return to.toMillis() - from.toMillis() <= ONE_DAY_MS ? 'hour' : 'day';
}

export type HomeHeadline =
  | { kind: 'total'; value: number }
  | { kind: 'point'; value: number; label: string; count: number };

/** The point under the finger/mouse, or the period total. */
export function resolveHomeHeadline(
  totalRevenue: number,
  rows: HomeChartRow[],
  activeIndex: number | null
): HomeHeadline {
  const row = activeIndex === null ? undefined : rows[activeIndex];
  if (!row) return { kind: 'total', value: totalRevenue };
  return {
    kind: 'point',
    value: row.revenue,
    label: row.month,
    count: row.count,
  };
}
